<template>
  <aside :class="`${currentSection}-page`">
    <div :id="currentSection" />
    <AppContentLoader v-if="loading" />

    <template v-else>
      <DocumentPageTitle
        :show-word-count="false"
        title="Annexures"
      />

      <div v-if="currentSection === 'required-annexures'">
        <h2>Required Annexures</h2>

        <div class="light-chrome-banner">
          <strong>Please Note:</strong> Required Annexures must be uploaded and can be cross-referenced in the document body, although the system allows publication even if some are missing. Clicking a reference link will open the annexure PDF in a new tab.
        </div>

        <AppTable>
          <AppTableHeader>
            <th class="w-[67px] text-center">
              Uploaded Status
            </th>

            <th>Document Name</th>
            <th>Document Link</th>
          </AppTableHeader>

          <AppTableBody>
            <tr
              v-for="annexureType in requiredAnnexureTypes"
              :key="annexureType.id"
            >
              <td class="text-center [&_svg]:w-4 [&_svg]:h-4 [&_svg]:p-0.5 [&_svg]:mt-1 [&_svg]:text-white">
                <FontAwesomeIcon
                  v-if="isAnnexureUploaded(annexureType.id)"
                  :icon="faCheck"
                  class="bg-success"
                />

                <FontAwesomeIcon
                  v-else
                  :icon="faTimes"
                  class="bg-editor-annexures-x-mark-bg"
                />
              </td>

              <td>{{ annexureType.name }}</td>

              <td>
                <DocumentAnnexureUploadButton
                  :annexure="getUploadedAnnexure(annexureType)"
                  :current-section="currentSection"
                  @saved="loadAnnexures"
                  @deleted="loadAnnexures"
                />
              </td>
            </tr>
          </AppTableBody>
        </AppTable>
      </div>

      <div v-else>
        <h3>Additional Annexures</h3>
        <div class="light-chrome-banner">
          <strong>Please Note:</strong> You may upload any supporting documents here. Please ensure all files are in PDF format and do not exceed 10MB per file.
        </div>

        <AppTable class="mb-4">
          <AppTableHeader>
            <th>Document Name</th>
            <th>Document Link</th>
          </AppTableHeader>

          <AppTableBody
            :no-results="additionalAnnexuresList.length === 0"
            no-results-text="No annexures uploaded"
          >
            <tr
              v-for="annexure in additionalAnnexuresList"
              :key="annexure.id"
            >
              <td>{{ annexure.name }}</td>

              <td>
                <DocumentAnnexureUploadButton
                  class="[&_.upload-button]:!hidden"
                  :annexure="annexure"
                  :current-section="currentSection"
                  @saved="loadAnnexures"
                  @deleted="loadAnnexures"
                />
              </td>
            </tr>
          </AppTableBody>
        </AppTable>

        <DocumentAnnexureUploadButton
          class="[&_.document-list-item]:!hidden [&_.document-actions]:!hidden"
          :current-section="currentSection"
          @saved="loadAnnexures"
        />
      </div>
    </template>
  </aside>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { computed, inject, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';

import { AppContentLoader } from '@/components/app';
import { DocumentPageTitle, DocumentAnnexureUploadButton } from '@/components/plan-document/components';
import { AppTable, AppTableHeader, AppTableBody } from '@/components/tables';

import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import { useHttp } from '@/composables';
import { usePlanStore } from '@/store';

const planStore = usePlanStore();

const route = useRoute();
const activeSectionId = inject('activeSectionId');

const loading = ref(true);

const currentSection = computed(() => {
  return activeSectionId?.value || 'required-annexures';
});

onMounted(async () => {
  await Promise.all([
    loadAnnexures(),
    planStore.loadAnnexureTypes(),
  ]);

  loading.value = false;
});

const loadAnnexures = async () => {
  loading.value = true;

  const response = await useHttp().get(`/municipalities/${route.params.municipality}/plan/annexures`);

  if (response.statusCode === HttpStatusCode.Ok) {
    planStore.planAnnexures = response.data.items || [];
  } else {
    console.error('Failed to load annexures');
  }
  loading.value = false;
};

const isAnnexureUploaded = (annexureTypeId) => {
  return planStore.planAnnexures.some(annexure => annexure.type?.id === annexureTypeId);
};

// Find document by ID
const getUploadedAnnexure = (annexureType) => {
  const found = planStore.planAnnexures.find(annexure => annexure.type?.id === annexureType.id);

  if (found) {
    return found;
  }

  // Return a placeholder object if not uploaded yet so the upload button knows what type to assign
  return {
    name: annexureType.name,
    type: annexureType,
  };
};

const requiredAnnexureTypes = computed(() => {
  return planStore.annexureTypes.filter(annexureType => annexureType.required);
});

// Filter for documents where the type is not required (i.e. Additional)
const additionalAnnexuresList = computed(() => {
  return planStore.planAnnexures
    .filter(annexure => !annexure.type?.required)
    .sort((a, b) => a.name.localeCompare(b.name));
});

</script>
